import React, { useRef, useEffect } from 'react';
import fsSource from '../shaders/fractal.vert';
import vsSource from '../shaders/fractal.frag';

const CanvasBackground: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const gl = canvas.getContext('webgl', { powerPreference: 'low-power' });
        if (!gl) {
            console.error('WebGL not supported');
            return;
        }

        let rect = canvas.getBoundingClientRect();

        const resizeCanvas = () => {
            rect = canvas.getBoundingClientRect();
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const width = Math.max(1, Math.round(rect.width * dpr));
            const height = Math.max(1, Math.round(rect.height * dpr));

            if (canvas.width !== width || canvas.height !== height) {
                canvas.width = width;
                canvas.height = height;
                gl.viewport(0, 0, canvas.width, canvas.height);
            }
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);
        window.addEventListener('orientationchange', resizeCanvas);

        const compileShader = (source: string, type: number) => {
            const shader = gl.createShader(type);
            if (!shader) throw new Error('Unable to create shader');
            gl.shaderSource(shader, source);
            gl.compileShader(shader);

            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                const log = gl.getShaderInfoLog(shader);
                gl.deleteShader(shader);
                throw new Error(`Shader compile error: ${log}`);
            }
            return shader;
        };

        const vertexShader = compileShader(vsSource, gl.VERTEX_SHADER);
        const fragmentShader = compileShader(fsSource, gl.FRAGMENT_SHADER);

        const program = gl.createProgram();
        if (!program) throw new Error('Unable to create program');
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            const log = gl.getProgramInfoLog(program);
            gl.deleteProgram(program);
            throw new Error(`Program link error: ${log}`);
        }

        gl.useProgram(program);

        const positionBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
        const positions = new Float32Array([
            -1, -1, 1, -1, -1, 1,
            -1, 1, 1, -1, 1, 1,
        ]);
        gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

        const positionLocation = gl.getAttribLocation(program, 'a_position');
        const resolutionUniformLocation = gl.getUniformLocation(program, 'resolution');
        const timeUniformLocation = gl.getUniformLocation(program, 'time');
        const mouseUniformLocation = gl.getUniformLocation(program, 'mouse');

        gl.enableVertexAttribArray(positionLocation);
        gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

        let mouse = [0, 0];
        const handleMouseMove = (event: MouseEvent) => {
            mouse = [
                (event.clientX - rect.left) / rect.width,
                1 - (event.clientY - rect.top) / rect.height,
            ];
        };

        window.addEventListener('mousemove', handleMouseMove);

        let animationFrameId: number;
        let running = true;

        const render = (time: number) => {
            if (!running) return;
            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.uniform2f(resolutionUniformLocation, canvas.width, canvas.height);
            gl.uniform2fv(mouseUniformLocation, mouse);
            gl.uniform1f(timeUniformLocation, time * 0.001);

            gl.drawArrays(gl.TRIANGLES, 0, 6);

            animationFrameId = requestAnimationFrame(render);
        };

        const onVisibilityChange = () => {
            running = document.visibilityState === 'visible';
            if (running) {
                render(performance.now());
            } else {
                cancelAnimationFrame(animationFrameId);
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);

        render(0);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('orientationchange', resizeCanvas);
            window.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            cancelAnimationFrame(animationFrameId);

            gl.deleteBuffer(positionBuffer);
            gl.deleteProgram(program);
            gl.deleteShader(vertexShader);
            gl.deleteShader(fragmentShader);
        };
    }, []);

    return <canvas ref={canvasRef} className="canvas-bg" />;
};

export default CanvasBackground;
